import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { cities } from '@/data/cities';
import { API } from '@/App';
import { MapPin, CheckCircle, Route, Users, Fuel, Settings } from 'lucide-react';

const CityPage = () => {
  const { citySlug } = useParams();
  const city = cities[citySlug];
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (city) fetchCars();
    // Update document title & meta for SEO
    if (city) {
      document.title = city.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', city.description);
    }
    return () => {
      document.title = 'Amjed Tours | Car Rental in Pakistan - Islamabad, Lahore, Karachi';
    };
  }, [citySlug]);

  const fetchCars = async () => {
    try {
      const response = await axios.get(`${API}/cars`);
      // Prioritize cars in that city, then others
      const cityCars = response.data.filter(c => c.location.toLowerCase() === city.name.toLowerCase());
      const otherCars = response.data.filter(c => c.location.toLowerCase() !== city.name.toLowerCase());
      setCars([...cityCars, ...otherCars]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!city) return <Navigate to="/" />;

  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="relative py-32 px-6 bg-primary text-white overflow-hidden" data-testid={`city-hero-${city.slug}`}>
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1770899235413-3c75535b018a?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="max-w-5xl mx-auto relative z-10 text-center">
          <p className="text-sm uppercase tracking-[0.2em] mb-3 font-mono opacity-90">Car Rental in</p>
          <h1 className="text-5xl sm:text-6xl font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="city-page-title">
            {city.name}
          </h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">{city.heroSubtitle}</p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 px-6" data-testid="city-intro">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Premium Car Rental in {city.name}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">{city.intro}</p>
        </div>
      </section>

      {/* Cars in this city */}
      <section className="py-16 px-6 bg-muted">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Available Cars in {city.name}
          </h2>
          {loading ? (
            <p>Loading cars...</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.slice(0, 6).map((car, index) => (
                <div key={car.id} className="car-card rounded-lg overflow-hidden bg-white" data-testid={`city-car-${index}`}>
                  <div className="aspect-video overflow-hidden">
                    <img src={car.image_url} alt={car.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <p className="text-xs uppercase tracking-wider text-accent font-mono mb-1">{car.type}</p>
                    <h3 className="text-xl font-bold mb-2">{car.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {car.location}
                    </p>
                    <p className="text-2xl font-bold text-primary mb-4">Rs. {car.price_per_day.toLocaleString()}<span className="text-sm font-normal text-muted-foreground">/day</span></p>
                    <Link to={`/cars/${car.id}`}>
                      <Button className="w-full btn-primary">View Details</Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Landmarks */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <MapPin className="w-8 h-8 text-accent" /> Top Landmarks
            </h2>
            <ul className="space-y-3">
              {city.landmarks.map((landmark, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>{landmark}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
              <Route className="w-8 h-8 text-accent" /> Popular Routes
            </h2>
            <ul className="space-y-3">
              {city.popularRoutes.map((route, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>{route}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-primary text-white text-center">
        <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
          Ready to Book Your Car in {city.name}?
        </h2>
        <p className="text-lg opacity-90 mb-6">Call us at 0311-9951299 or browse online</p>
        <Link to="/cars">
          <Button className="bg-accent hover:bg-accent/90 text-white text-lg px-8 py-6 rounded-full">
            Browse Cars
          </Button>
        </Link>
      </section>

      <Footer />
    </div>
  );
};

export default CityPage;
